import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_URL } from '../utils/api';
import { LeafIcon } from '../components/ui/Icons';

const ResetPassword = () => {
    const { token } = useParams();
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 8) {
            setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Die Passwörter stimmen nicht überein.');
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/password-reset/reset/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, new_password: newPassword }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data.detail || 'Ungültiger oder abgelaufener Link.');
            }
            setSuccess(true);
            setTimeout(() => navigate('/auth'), 3000);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative min-h-screen overflow-hidden flex items-center justify-center bg-gradient-to-br from-[#f5fff8] via-[#e7f6eb] to-[#f9fff5] py-12 px-4 sm:px-6 dark:bg-[#0d0f12]">
            {/* Background decorations */}
            <div className="pointer-events-none absolute inset-0">
                <svg viewBox="0 0 320 320" className="absolute -top-24 -left-20 w-72 h-72 opacity-60" style={{ animation: 'leafFloat1 18s ease-in-out infinite' }} aria-hidden="true">
                    <defs>
                        <linearGradient id="pwResetGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#d7f2df" />
                            <stop offset="100%" stopColor="#bfe8c9" />
                        </linearGradient>
                    </defs>
                    <path d="M0 260 L90 110 L160 220 L220 140 L320 260 Z" fill="url(#pwResetGrad)" />
                    <circle cx="230" cy="120" r="32" fill="#dff4e7" opacity="0.6" />
                </svg>
                <svg viewBox="0 0 240 240" className="absolute bottom-[-80px] right-[-60px] w-80 h-80 opacity-55" style={{ animation: 'leafFloat3 20s ease-in-out infinite' }} aria-hidden="true">
                    <path d="M20 220 C40 140 80 60 120 40 C180 10 210 100 220 180 Z" fill="#cbeed7" />
                </svg>
            </div>

            <div className="relative z-10 w-full max-w-md">
                <div className="card p-8 sm:p-10 bg-gradient-to-br from-plantlix-50 to-gray-100 dark:from-plantlix-900/30 dark:to-gray-800 border border-plantlix-200/50 dark:border-plantlix-700/50 shadow-xl backdrop-blur">

                    <div className="text-center mb-7">
                        <LeafIcon color="currentColor" className="mx-auto w-12 h-12" />
                        <h1 className="mt-4 text-2xl font-bold text-gray-700 dark:text-gray-100">
                            Neues Passwort setzen
                        </h1>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-300">
                            Mindestens 8 Zeichen.
                        </p>
                    </div>

                    {success ? (
                        <div className="text-center">
                            <div className="text-5xl mb-4">✅</div>
                            <p className="text-plantlix-700 dark:text-plantlix-300 font-semibold text-lg mb-2">
                                Passwort erfolgreich geändert!
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Du wirst in wenigen Sekunden zur Anmeldung weitergeleitet…
                            </p>
                            <button
                                onClick={() => navigate('/auth')}
                                className="mt-5 w-full py-3 px-4 rounded-lg bg-plantlix-500 hover:bg-plantlix-600 text-white font-semibold transition-all"
                            >
                                Jetzt anmelden
                            </button>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <p className="bg-red-50/90 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700 p-3.5 rounded-lg text-center mb-4 text-sm">
                                    {error}
                                </p>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {/* New password */}
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1">
                                        Neues Passwort
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            required
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-black/20 focus:outline-none focus:ring-2 focus:ring-plantlix-500 focus:border-plantlix-500 transition-all shadow-sm pr-10"
                                            placeholder="••••••••"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-transparent border-none cursor-pointer focus:outline-none"
                                        >
                                            {showPassword ? (
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                </svg>
                                            ) : (
                                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Confirm password */}
                                <div className="space-y-1">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 ml-1">
                                        Passwort bestätigen
                                    </label>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        required
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-black/20 focus:outline-none focus:ring-2 focus:ring-plantlix-500 focus:border-plantlix-500 transition-all shadow-sm"
                                        placeholder="••••••••"
                                    />
                                </div>

                                {/* Password strength hint */}
                                {newPassword && (
                                    <div className="flex items-center gap-2 text-xs">
                                        <div className={`h-1.5 flex-1 rounded-full transition-all ${newPassword.length < 8 ? 'bg-red-400' : newPassword.length < 12 ? 'bg-yellow-400' : 'bg-plantlix-500'}`} />
                                        <span className={`${newPassword.length < 8 ? 'text-red-600' : newPassword.length < 12 ? 'text-yellow-600' : 'text-plantlix-600'} dark:${newPassword.length < 8 ? 'text-red-400' : newPassword.length < 12 ? 'text-yellow-400' : 'text-plantlix-400'}`}>
                                            {newPassword.length < 8 ? 'Zu kurz' : newPassword.length < 12 ? 'Ausreichend' : 'Stark'}
                                        </span>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-lg shadow-sm text-base font-semibold text-white bg-plantlix-500 hover:bg-plantlix-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-plantlix-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                                >
                                    {loading ? (
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                                    ) : (
                                        'Passwort speichern'
                                    )}
                                </button>
                            </form>

                            <div className="mt-5 text-center">
                                <button
                                    onClick={() => navigate('/auth')}
                                    className="text-sm font-medium text-plantlix-500 hover:text-[#1a6b1a] transition-colors hover:underline"
                                >
                                    ← Zurück zur Anmeldung
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResetPassword;
